import React, { useState, useEffect } from "react";
import { User } from "../types/user.types";
import useCurrentUser from "../hooks/useCurrentUser";
import UserDetails from "./UserDetails";
import UserProfileCard from "./UserProfileCard";
import { updateUser } from "../services/userService";
import { useAuth } from "../../../context/AuthContext";
import { CognitoAuthService } from "../../../services/cognitoService";
import { debug } from "../../../utils/debugLogger";

const UserList: React.FC = () => {
  const { user: dbUser, loading, error, idToken, refetch } = useCurrentUser();
  // Use the same auth context that UserRoleInfo uses
  const { user: cognitoUser, userGroups } = useAuth();
  const [createLoading, setCreateLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Refetch user when returning from Stripe portal (e.g. after cancellation)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("return") && params.get("return") === "billing") {
      refetch();
      // Remove param from URL without full reload
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [refetch]);

  const handleSaveProfile = async (userData: Partial<User>) => {
    if (!idToken) {
      setSaveError("Authentication required");
      return;
    }

    try {
      setCreateLoading(true);
      setSaveError(null);

      // Get user ID from auth context (same as UserRoleInfo uses)
      const userId = CognitoAuthService.getUserId(idToken) || "";

      if (!userId) {
        throw new Error("Missing required user information");
      }

      // Format user ID correctly
      const formattedUserId = userId.startsWith("user_id_")
        ? userId
        : `user_id_${userId}`;

      // Create user with form data + Cognito data
      const nowIso = new Date().toISOString();
      const newUser = {
        user_id: formattedUserId,
        email: userData.email,
        email_lc: (userData.email || "").toLowerCase(),
        display_name: userData.display_name,
        status: userData.status || "ACTIVE",
        // Convert from array to string for API
        groups:
          typeof userData.groups === "string"
            ? userData.groups
            : userGroups?.length
            ? userGroups[0]
            : "Customer",
        subscription_tier: userData.subscription_tier || "DAISY_LIGHT",
        created_at: nowIso,
        updated_at: nowIso,
      };

      await updateUser(newUser as User, idToken);

      // Refresh the page to load the new user
      window.location.reload();
    } catch (err: any) {
      setSaveError(err.message || "Failed to save profile");
      debug.error("Error saving profile:", err);
      throw err; // Re-throw so the form can handle the error
    } finally {
      setCreateLoading(false);
    }
  };

  // Build skeletonUser for new user creation (when dbUser is not found)
  const skeletonUser = (() => {
    if (!idToken) return undefined;
    const userId = CognitoAuthService.getUserId(idToken) || "";
    const email = CognitoAuthService.getUserEmail(idToken) || "";
    const displayName = CognitoAuthService.extractDisplayName(
      CognitoAuthService.parseTokenPayload(idToken)
    );
    const nowIso = new Date().toISOString();

    // Ensure user_id is properly formatted with the prefix
    const formattedUserId = userId.startsWith("user_id_")
      ? userId
      : `user_id_${userId}`;

    return {
      user_id: formattedUserId,
      email,
      email_lc: email.toLowerCase(),
      display_name: displayName,
      status: "ACTIVE",
      // Convert from array to string for API
      groups: userGroups?.length ? userGroups[0] : "Customer",
      subscription_tier: "DAISY_LIGHT",
      created_at: nowIso,
      updated_at: nowIso,
    };
  })();

  // Show loading state
  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-brand-primary" />
      </div>
    );
  }

  // Special case: User exists in Cognito but not in DB
  // Show profile completion form
  if (error === "USER_NOT_IN_DB" || !dbUser) {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-6">
          <div className="mb-8 flex items-center justify-between px-0.5">
            <div className="flex items-center space-x-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-zinc-200/90 bg-white shadow-sm">
                <span className="material-icons-outlined text-3xl text-brand-primary-light">
                  account_circle
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-brand-primary">
                  Complete Your Profile
                </h1>
                <p className="text-sm font-medium text-zinc-500">
                  A few details to finish setting up your account
                </p>
              </div>
            </div>
            {userGroups?.length ? (
              <span className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-700 shadow-sm">
                {userGroups[0]}
              </span>
            ) : null}
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  {error === "USER_NOT_IN_DB"
                    ? "Your account is set up, but you need to complete your profile. We've pre-filled some details from your login."
                    : "Please complete your profile information below. We've pre-filled some details from your login."}
                </p>
              </div>
            </div>
          </div>

          {saveError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
              {saveError}
            </div>
          )}

          <UserDetails
            user={skeletonUser}
            canEditGroups={false}
            isEdit={true}
            onSave={handleSaveProfile}
          />
      </div>
    );
  }

  // Show other errors (not USER_NOT_IN_DB)
  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          {error}
        </div>
      </div>
    );
  }

  return <UserProfileCard user={dbUser} idToken={idToken} />;
};

export default UserList;
